import { useEffect, useState } from 'react';
import { Bell, Moon, BellRing, Loader2 } from 'lucide-react';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { notifPrefsAPI } from '../lib/api';
import { toast } from 'sonner';
import { devError } from '../lib/devLog';

const NOTIF_TYPES = [
  { value: 'job_status', label: 'Job updates (accepted, completed, etc.)' },
  { value: 'new_message', label: 'New chat messages' },
  { value: 'friend_request', label: 'Friend requests' },
  { value: 'task_bid', label: 'Task bids' },
  { value: 'community', label: 'Community posts' },
  { value: 'dispute_opened', label: 'Disputes (admins)' },
  { value: 'dispute_update', label: 'Dispute updates' },
  { value: 'refund_issued', label: 'Refund confirmations' },
];

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const fmtHour = (h) => {
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:00 ${h < 12 ? 'AM' : 'PM'}`;
};

const NotificationPreferences = ({ open, onOpenChange }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [prefs, setPrefs] = useState({
    push_enabled: true,
    quiet_hours_enabled: false,
    quiet_hours_start: 22,
    quiet_hours_end: 7,
    muted_types: [],
  });

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    notifPrefsAPI.get()
      .then(res => setPrefs({
        push_enabled: res.data.push_enabled ?? true,
        quiet_hours_enabled: res.data.quiet_hours_enabled ?? false,
        quiet_hours_start: res.data.quiet_hours_start ?? 22,
        quiet_hours_end: res.data.quiet_hours_end ?? 7,
        muted_types: res.data.muted_types || [],
      }))
      .catch(e => { devError(e); toast.error('Failed to load preferences'); })
      .finally(() => setLoading(false));
  }, [open]);

  const toggleType = (typeValue) => {
    setPrefs(p => ({
      ...p,
      muted_types: p.muted_types.includes(typeValue)
        ? p.muted_types.filter(t => t !== typeValue)
        : [...p.muted_types, typeValue],
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await notifPrefsAPI.update(prefs);
      toast.success('Preferences saved');
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="notif-prefs-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-orange-500" />
            Notification Preferences
          </DialogTitle>
          <DialogDescription>
            Control when and how you receive notifications.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-orange-500" />
          </div>
        ) : (
          <div className="space-y-5">
            {/* Master toggle */}
            <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-zinc-50">
              <div className="flex items-center gap-2">
                <BellRing className="w-4 h-4 text-zinc-600" />
                <div>
                  <Label htmlFor="push-master" className="cursor-pointer font-medium">Push notifications</Label>
                  <p className="text-xs text-zinc-500">Master switch for all push alerts</p>
                </div>
              </div>
              <Switch
                id="push-master"
                checked={prefs.push_enabled}
                onCheckedChange={(v) => setPrefs(p => ({ ...p, push_enabled: v }))}
                data-testid="push-enabled-switch"
              />
            </div>

            {/* Quiet hours */}
            <div className={`space-y-3 p-3 rounded-lg ${prefs.push_enabled ? 'bg-zinc-50' : 'bg-zinc-50 opacity-60 pointer-events-none'}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Moon className="w-4 h-4 text-zinc-600" />
                  <div>
                    <Label htmlFor="quiet-toggle" className="cursor-pointer font-medium">Quiet hours</Label>
                    <p className="text-xs text-zinc-500">No pushes during these hours</p>
                  </div>
                </div>
                <Switch
                  id="quiet-toggle"
                  checked={prefs.quiet_hours_enabled}
                  onCheckedChange={(v) => setPrefs(p => ({ ...p, quiet_hours_enabled: v }))}
                  data-testid="quiet-hours-switch"
                />
              </div>
              {prefs.quiet_hours_enabled && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-zinc-600">From</Label>
                    <Select
                      value={String(prefs.quiet_hours_start)}
                      onValueChange={(v) => setPrefs(p => ({ ...p, quiet_hours_start: parseInt(v, 10) }))}
                    >
                      <SelectTrigger className="h-9 mt-1" data-testid="quiet-start-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {HOURS.map(h => <SelectItem key={h} value={String(h)}>{fmtHour(h)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-zinc-600">Until</Label>
                    <Select
                      value={String(prefs.quiet_hours_end)}
                      onValueChange={(v) => setPrefs(p => ({ ...p, quiet_hours_end: parseInt(v, 10) }))}
                    >
                      <SelectTrigger className="h-9 mt-1" data-testid="quiet-end-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {HOURS.map(h => <SelectItem key={h} value={String(h)}>{fmtHour(h)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>

            {/* Per-type toggles */}
            <div className={`space-y-2 ${prefs.push_enabled ? '' : 'opacity-60 pointer-events-none'}`}>
              <p className="text-sm font-medium text-zinc-700">Mute specific types</p>
              <div className="space-y-1.5">
                {NOTIF_TYPES.map(t => {
                  const muted = prefs.muted_types.includes(t.value);
                  return (
                    <label
                      key={t.value}
                      className="flex items-center justify-between p-2 rounded hover:bg-zinc-50 cursor-pointer"
                      data-testid={`type-toggle-${t.value}`}
                    >
                      <span className="text-sm text-zinc-700">{t.label}</span>
                      <Switch
                        checked={!muted}
                        onCheckedChange={() => toggleType(t.value)}
                      />
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-3 border-t border-zinc-100">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button
            onClick={save}
            disabled={loading || saving}
            className="bg-orange-500 hover:bg-orange-600 text-white"
            data-testid="save-prefs-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            Save Preferences
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NotificationPreferences;
